import {
  useRef,
  useState,
} from 'react';

import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faArrowLeft,
} from '@fortawesome/pro-regular-svg-icons';

import {
  useAppDispatch,
  useAppSelector,
  toggleEditingSettings,
  mapSelector,
  markersSelector,
  setMarkers,
  setMarkerId,
  linkedLayersSelector,
  setMapSettings,
  updatePreference,
  preferencesSelector,
  MarkerType,
  ImageType,
} from '../../../store';

import { MarkerUpload } from '../../spaces/Sidebar/Edit/MarkerUpload';
import { ImageUploader } from '../../spaces/Sidebar/Edit/ImageUploader';
import { EditStyle } from './EditStyle';

import './EditSettings.scss';

export function EditSettings() {
  const dispatch = useAppDispatch();
  const iMap = useAppSelector(mapSelector);
  const preferences = useAppSelector(preferencesSelector);
  const form = useRef(null);
  const linkedLayers = useAppSelector(linkedLayersSelector);

  // Name for firstItemOpen label and title
  const [groupByName, setGroupByName] = useState(
    iMap.groupBy === 'categories' ? 'Categories' : 'Floors or Levels'
  );

  // Top-left corner image
  const [featuredImage, setFeaturedImage] = useState(iMap.featured_image);

  // Space markers icon and size
  const markers = useAppSelector(markersSelector);
  const [selectedMarker, setSelectedMarker] = useState(
    markers.find((m) => m.id === iMap.markerId) ?? iMap.defaultMarkers
  );

  const save = () => {
    if (!form.current) return;
    const formData = new FormData(form.current);
    const formValues = Object.fromEntries(formData.entries());
    const mapSettings = {
      allowLabels: formValues.allowLabels === 'true',
      groupBy: formValues.groupBy || "categories",
      featured_video: formValues.featured_video,
      featured_image: featuredImage,
      markerId: selectedMarker.id,
    };
    dispatch(setMapSettings(mapSettings));
    dispatch(updatePreference({
      key: 'sidebarOpenItems',
      value: formValues.sidebarOpenItems.toString(),
    }));
    dispatch(updatePreference({
      key: 'showWaypoints',
      value: formValues.showWaypoints === 'true',
    }));
    dispatch(updatePreference({
      key: 'wayfindingButton',
      value: formValues.wayfindingButton === 'true',
    }));
    dispatch(updatePreference({
      key: 'wayfindingDropdowns',
      value: formValues.wayfindingDropdowns === 'true',
    }));
    dispatch(toggleEditingSettings());
  };

  return (
    <form
      id="edit-map-settings"
      ref={form}
      method="dialog"
      onSubmit={save}
    >
      <section className="edit-settings">
        <header>
          <button
            onClick={() => dispatch(toggleEditingSettings())}
          >
            <FontAwesomeIcon
              icon={faArrowLeft}
              size="xl"
            />
          </button>
          <h3>Settings</h3>
        </header>
        <ul>
          {linkedLayers.length > 1 && (
            <li
              title="Show Spaces by Category or by Floors / Levels"
            >
              <label>
                <input
                  name="groupBy"
                  type="radio"
                  value="categories"
                  defaultChecked={iMap.groupBy === 'categories'}
                  onChange={() => setGroupByName('Category')}
                />
                Categories
              </label>
              <label>
                <input
                  name="groupBy"
                  type="radio"
                  value="layers"
                  defaultChecked={iMap.groupBy === 'layers'}
                  onChange={() => setGroupByName('Floor')}
                />
                Floors or Levels
              </label>
            </li>
          )}
          <li
            title={`Status of ${groupByName} in the sidebar when loading the map.`}
          >
            <div className="tw:ml-3">
              <span>Load Map with {groupByName}:</span>
              <label>
                <input
                  name="sidebarOpenItems"
                  type="radio"
                  value="none"
                  defaultChecked={iMap.preferences.sidebarOpenItems === 'none'}
                />
                <span>All Closed</span>
              </label>
              <label>
                <input
                  name="sidebarOpenItems"
                  type="radio"
                  value="first"
                  defaultChecked={iMap.preferences.sidebarOpenItems === 'first'}
                />
                <span>First Open</span>
              </label>
              <label>
                <input
                  name="sidebarOpenItems"
                  type="radio"
                  value="all"
                  defaultChecked={iMap.preferences.sidebarOpenItems === 'all'}
                />
                <span>All Open</span>
              </label>
            </div>
          </li>
          <li
            className="video"
            title="This video will be displayed at the top of the sidebar."
          >
            <label>
              <span>Featured Video URL</span>
              <input
                name="featured_video"
                type="text"
                defaultValue={iMap.featured_video}
              />
            </label>
          </li>
          <li
            className="video"
            title="This video will be displayed at the top of the sidebar."
          >
            <label>
              <ImageUploader
                label="Featured Image"
                images={featuredImage ? [featuredImage] : []}
                setImages={(images: ImageType[]) => setFeaturedImage(images[0])}
                maxSize={1_000_000} // 1 MB
                multiple={false}
              />
            </label>
          </li>
          <hr />
          <li className="markers">
            <MarkerUpload
              selectedMarker={selectedMarker}
              setSelectedMarker={(m: MarkerType) => {
                setSelectedMarker(m);
                dispatch(setMarkerId(m.id));
              }}
              markers={markers}
              setMarkers={(markers) => dispatch(setMarkers(markers))}
            />
          </li>
          <EditStyle />
          <hr />
          <li
            className="wayfinding-settings"
            title="Show or hide waypoint nodes while editing navigation paths"
          >
            <h4>Wayfinding</h4>
            <label>
              <input
                name="showWaypoints"
                type="checkbox"
                value="true"
                defaultChecked={preferences.showWaypoints ?? true}
              />
              <span>Show waypoints in edit mode</span>
            </label>
            <label>
              <input
                name="wayfindingButton"
                type="checkbox"
                value="true"
                defaultChecked={preferences.wayfindingButton ?? true}
              />
              <span>Map Wayfinding</span>
            </label>
            <label>
              <input
                name="wayfindingDropdowns"
                type="checkbox"
                value="true"
                defaultChecked={preferences.wayfindingDropdowns ?? true}
              />
              <span>Marker Wayfinding</span>
            </label>
          </li>
          <li
            title="Locations are map markers with a name only"
          >
            <h4>Labels</h4>
            <label>
              <input
                name="allowLabels"
                type="checkbox"
                value="true"
                defaultChecked={iMap.allowLabels}
              />
              <span>Enable Labels</span>
            </label>
          </li>
        </ul>
        <footer>
          <input
            type="button"
            value="Cancel"
            onClick={() => {
              dispatch(toggleEditingSettings());
            }}
          />
          <input
            type="submit"
            value="Ok"
          // Handled on form: onSubmit={save}
          />
        </footer>
      </section>
    </form>
  );
}
